import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";
import {
  createMatterSettlementDemandV1,
  deleteMatterSettlementDemandV1,
  updateMatterSettlementDemandV1,
} from "@/lib/data/requests/matters/matter-settlement-demand";

export const useCreateMatterSettlementDemand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterSettlementDemandV1>[0]) =>
      createMatterSettlementDemandV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              settlementDemands: [
                ...oldData.data.settlementDemands,
                {
                  ...data,
                  id: "optimistic",
                },
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toast.error("Error creating settlement demand");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Settlement demand created successfully");
    },
  });
};

export const useUpdateMatterSettlementDemand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterSettlementDemandV1>[0]) =>
      updateMatterSettlementDemandV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              settlementDemands: oldData.data.settlementDemands.map((demand) =>
                demand.id === data.id
                  ? {
                      ...demand,
                      ...data,
                    }
                  : demand,
              ),
            },
          };
        },
      );
      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating settlement demand");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Settlement demand updated successfully");
    },
  });
};

export const useDeleteMatterSettlementDemand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterSettlementDemandV1>[0]) =>
      deleteMatterSettlementDemandV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              settlementDemands: oldData.data.settlementDemands.filter(
                (demand) => demand.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting settlement demand");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Settlement demand deleted successfully");
    },
  });
};
